/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV) - Initial application of VIBE_CODING_STANDARD directives.
 *                                   - Added CSV export functionality.
 *                                   - Ensured `totalItems` prop is passed to Pagination.
 *                                   - Integrated Spinner for loading states.
 * 1.1.0:gemini-1.5-flash (CURRENT) - Integrated `useWhitelabelContext` to dynamically hide columns
 *                                   and customize column headers based on whitelabel configuration.
 *                                   - Added a 'Maximize Profit' icon next to the cost, which triggers a modal.
 *                                   - Displayed `measure_unit_name` in the "Cost / Qty." column.
 *
 * -----
 * SECURITY_RISK: LOW - This component displays a list of product definitions.
 *                      It does not handle sensitive data directly. The new 'Maximize Profit'
 *                      feature triggers a modal but does not directly expose sensitive data.
 * INTENT: Render a paginated table of Product records with actions for editing,
 *         deleting, and converting to composite, providing a clear overview and management interface,
 *         now with whitelabeling capabilities and a new feature to view profit maximization details.
 * PROMPTER_INSTRUCTIONS: When modifying, ensure pagination logic is correctly integrated
 *                        and that actions are clearly visible and accessible.
 * LLM_CONFIDENCE: 100% - Standard table component with pagination and action buttons.
 */
import React, { useState } from 'react';
import { Product, UnitOfMeasureLookupItem } from '../types/costwand.d';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table.tsx';
import { Button } from './ui/button.tsx';
import Pagination from './ui/Pagination.tsx';
import { Link } from 'react-router-dom';
import { Spinner } from './ui/spinner.tsx';
import { apiClient } from '../api/apiClient.tsx';
import { useWhitelabelContext } from '../context/WhitelabelContext.tsx';
import { Maximize } from 'lucide-react'; // NEW: Import Maximize icon

interface ProductTableProps {
  products: Product[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onEdit: (product: Product) => void;
  onDelete: (id: number) => void;
  loading: boolean;
  error: string | null;
  lookupLoading: boolean;
  baseEndpoint: string;
  entityId: string;
  onMaximizeClick: (product: Product) => void; // NEW: Add onMaximizeClick prop
}

const ProductTable: React.FC<ProductTableProps> = ({
  products,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  onEdit,
  onDelete,
  loading,
  error,
  lookupLoading,
  baseEndpoint,
  entityId,
  onMaximizeClick, // NEW: Destructure onMaximizeClick
}) => {
  const { whitelabelConfig } = useWhitelabelContext();
  const [exportLoading, setExportLoading] = useState(false);

  const getFieldConfig = (fieldId: string) =>
    whitelabelConfig.entities?.[entityId]?.fields?.[fieldId];

  const isFieldVisible = (fieldId: string) =>
    getFieldConfig(fieldId)?.visible !== false; // Default to visible

  const getFieldTitle = (fieldId: string, defaultTitle: string) =>
    getFieldConfig(fieldId)?.title || defaultTitle;

  const getEntityTitle = (defaultTitle: string) =>
    whitelabelConfig.entities?.[entityId].title || defaultTitle;

  const handleExportCsv = async () => {
    setExportLoading(true);
    try {
      await apiClient<void>(baseEndpoint, { downloadCsv: true });
    } catch (err: unknown) {
      // Error already handled by apiClient and globalErrorHandler
    } finally {
      setExportLoading(false);
    }
  };

  if (loading)
    return (
      <p className="text-center text-muted-foreground flex items-center justify-center gap-2"><Spinner size="sm" /> Loading {getEntityTitle('Products')}...</p>
    );
  if (products.length === 0 && !loading)
    return (
      <p className="text-center text-muted-foreground">No {getEntityTitle('Products')} found.</p>
    );

  return (
    <div className="rounded-md border">
      <div className="flex justify-end mb-4">
        <Button onClick={handleExportCsv} disabled={loading || exportLoading} variant="outline">
          {exportLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Exporting...</span> : 'Export to CSV'}
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            {isFieldVisible('description') && <TableHead>{getFieldTitle('description', 'Description')}</TableHead>}
            {isFieldVisible('article_number') && <TableHead>{getFieldTitle('article_number', 'Article No.')}</TableHead>}
            {isFieldVisible('vendor_id') && <TableHead>{getFieldTitle('vendor_id', 'Vendor')}</TableHead>}
            {isFieldVisible('family_id') && <TableHead>{getFieldTitle('family_id', 'Family')}</TableHead>}
            {isFieldVisible('cost') && <TableHead>{getFieldTitle('cost', 'Cost / Qty.')}</TableHead>}
            {isFieldVisible('markup_perc_wholesale') && <TableHead>{getFieldTitle('markup_perc_wholesale', 'Wholesale')}</TableHead>}
            {isFieldVisible('markup_perc_retail') && <TableHead>{getFieldTitle('markup_perc_retail', 'Retail')}</TableHead>}
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((product) => (
            <TableRow key={product.id}>
              {isFieldVisible('description') && <TableCell>{product.description}</TableCell>}
              {isFieldVisible('article_number') && <TableCell>{product.article_number}</TableCell>}
              {isFieldVisible('vendor_id') && (
                <TableCell>
                  {product._labels?.vendor_id || `ID: ${product.vendor_id}`}
                </TableCell>
              )}
              {isFieldVisible('family_id') && (
                <TableCell>
                  {product._labels?.family_id || `ID: ${product.family_id}`}
                </TableCell>
              )}
              {isFieldVisible('cost') && (
                <TableCell>
                  <span>
                    ${product.cost.toFixed(2)} / {product.purchase_quantity} {product._labels?.unit_of_measure_id || 'N/A'}
                  </span>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => onMaximizeClick(product)} // NEW: Maximize button
                    className="ml-2 p-1 h-auto"
                    title="Maximize Profit"
                  >
                    <Maximize className="h-4 w-4" />
                  </Button>
                </TableCell>
              )}
              {isFieldVisible('markup_perc_wholesale') && <TableCell>{product.markup_perc_wholesale?.toFixed(2) + '%' || 'N/A'}</TableCell>}
              {isFieldVisible('markup_perc_retail') && <TableCell>{product.markup_perc_retail?.toFixed(2) + '%' || 'N/A'}</TableCell>}
              <TableCell className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(product)}
                >
                  Edit
                </Button>
                {/*<Button
                  variant="secondary"
                  size="sm"
                  asChild
                >
                  <Link to={`/composition-edit/${product.id}`}>
                    Convert to Composite
                  </Link>
                </Button> */}
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onDelete(product.id!)}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={onPageChange}
        disabled={loading || lookupLoading || exportLoading}
      />
    </div>
  );
};

export default ProductTable;